import React from 'react';
import {Controller, SubmitHandler} from 'react-hook-form';
import {t} from '@lingui/core/macro';

import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import InputText from 'shared/ui/Input/InputText';
import MultiSelectWithCheckbox from 'shared/ui/Input/MultiSelectWithCheckbox/MultiSelectWithCheckbox';
import DateRangePickerControlled from 'shared/ui/Date/DateRangePickerControlled/DateRangePickerControlled';
import OrderByControlled from 'shared/ui/OrderBy/OrderByControlled';
import Select from 'shared/ui/Select/Select';
import {IDomainIncidentsFiltersForm} from 'features/domain/model/DomainIncident/IDomainIncidentsFiltersForm';
import useDomainIncidentsFiltersForm from 'features/domain/model/useDomainIncidentsFiltersForm';
import {stringsToDateRange} from 'shared/lib/filterHelper';

interface IDomainIncidentsFiltersFormProps {
    onSubmit: SubmitHandler<IDomainIncidentsFiltersForm>;
    button: React.ReactElement;
}

const DomainIncidentsFiltersForm: React.FC<IDomainIncidentsFiltersFormProps> = ({onSubmit, button}) => {
    const {form, filters, sort, domainMonitorOptions, typeOptions, isMonitorSearchable} =
        useDomainIncidentsFiltersForm();
    const {register, handleSubmit, control} = form;

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBodyWrapper>
                <FormGroupWithError
                    label={t`Домен или его часть`}
                >
                    <InputText
                        register={register}
                        defaultValue={filters?.domain}
                        name="domain"
                        placeholder={t`Введите домен`}
                        control={control}
                    />
                </FormGroupWithError>

                <FormGroupWithError
                    label={t`Монитор домена`}
                >
                    <Select
                        control={control}
                        name="domainMonitor"
                        data={domainMonitorOptions}
                        searchable={isMonitorSearchable}
                    />
                </FormGroupWithError>

                <FormGroupWithError
                    label={t`Причина`}
                >
                    <Controller
                        name="type"
                        control={control}
                        render={({field, fieldState}) => (
                            <MultiSelectWithCheckbox
                                {...field}
                                value={field.value || []}
                                onChange={(selectedValues: string[]) => field.onChange(selectedValues)}
                                placeholder={t`Выберите причины`}
                                fieldState={fieldState}
                                options={typeOptions}
                            />
                        )}
                    />
                </FormGroupWithError>

                <FormGroupWithError
                    label={t`Дата возникновения`}
                >
                    <DateRangePickerControlled
                        control={control}
                        name="created_at"
                        defaultValue={stringsToDateRange(
                            filters['created_at[after]'],
                            filters['created_at[before]'],
                        )}
                        cleanable={true}
                    />
                </FormGroupWithError>

                <FormGroupWithError
                    label={t`Решить до`}
                >
                    <DateRangePickerControlled
                        control={control}
                        name="expire_at"
                        defaultValue={stringsToDateRange(
                            filters['expire_at[after]'],
                            filters['expire_at[before]'],
                        )}
                        cleanable={true}
                    />
                </FormGroupWithError>

                <FormGroupWithError
                    label={t`Дата решения`}
                >
                    <DateRangePickerControlled
                        control={control}
                        name="resolved_at"
                        defaultValue={stringsToDateRange(
                            filters['resolved_at[after]'],
                            filters['resolved_at[before]'],
                        )}
                        cleanable={true}
                    />
                </FormGroupWithError>

                <FormGroupWithError
                    label={t`Сортировка`}
                >
                    <OrderByControlled
                        control={control}
                        name="orderBy"
                        defaultValue={sort}
                        data={[
                            {label: t`Домен`, value: 'domain'},
                            {label: t`Причина`, value: 'type'},
                            {label: t`Дата возникновения`, value: 'created_at'},
                            {label: t`Решить до`, value: 'expire_at'},
                            {label: t`Дата решения`, value: 'resolved_at'},
                        ]}
                    />
                </FormGroupWithError>

            </ModalBodyWrapper>
            {button}
        </form>
    );
};

export default DomainIncidentsFiltersForm;
